/**
 * PendingWaiversBanner — tells a signed-in parent there is a waiver to sign.
 *
 * Since 2026-10-08 it is a pop-up (like the terms pop-up) rather than a strip
 * across the top of the page. The file and component keep their name so the
 * layout that mounts it did not have to change.
 *
 * The rules, in order of importance:
 *
 *   1. It never blocks paying. It does not open on a payment page, it closes
 *      itself if the parent lands on one, and it does not open on top of
 *      another dialog (a pay dialog on some other page).
 *   2. "Later" always clears it. So do the ✕ and the Escape key. Nothing has
 *      to be signed, read or scrolled first.
 *   3. It shows once per sign-in. It is marked as shown the moment it opens,
 *      not when it is dismissed, so a reload or a crash can never bring it
 *      straight back. (A blocking banner here once looped forever because
 *      the "seen" record was written after the escape action, 2026-09-24.)
 *   4. If the check for forms fails it shows nothing at all.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FileText } from "lucide-react";
import { Button } from "../ui";
import Modal from "../common/Modal";
import WaiverSignForm from "./WaiverSignForm";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import {
  getMyPendingWaivers,
  signMyWaiver,
  waiverErrorMessage,
  PendingWaiver,
  SignAnswers,
} from "../../api/waiverRequestsAPI";

/** sessionStorage key holding the sign-in the pop-up was last shown for. */
export const SHOWN_KEY = "pendingWaiversShownFor";

/**
 * Identifies the current sign-in: the user plus the session-start time the
 * backend puts in the token (`st`, which stays the same while the token is
 * refreshed and changes on a new sign-in). Without a readable token it falls
 * back to one fixed value, which simply means "once per browser tab".
 */
const currentSignIn = (): string => {
  try {
    const token = localStorage.getItem("authToken") || "";
    const body = token.split(".")[1];
    if (!body) return "signed-in";
    const payload = JSON.parse(atob(body.replace(/-/g, "+").replace(/_/g, "/")));
    const id = `${payload?.sub || ""}:${payload?.st || ""}`;
    return id === ":" ? "signed-in" : id;
  } catch {
    return "signed-in";
  }
};

const wasShownThisSignIn = (): boolean => {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === currentSignIn();
  } catch {
    return false;
  }
};

const markShown = () => {
  try {
    sessionStorage.setItem(SHOWN_KEY, currentSignIn());
  } catch {
    /* storage blocked: the in-memory flag still holds for this page */
  }
};

/**
 * True on any page where a parent pays or manages how they pay. Tested
 * against the path WITHOUT the club's own slug, so a club whose name
 * happens to contain "pay" is not treated as one long payment page.
 */
export const isPaymentPath = (pathname: string): boolean => {
  const rest = pathname.split("/").filter(Boolean).slice(1).join("/");
  return /(pay|billing|invoice|checkout|cart|update-card)/i.test(rest);
};

/** Another dialog (for example a pay dialog) is already on screen. */
const anotherDialogIsOpen = (): boolean => {
  if (typeof document === "undefined") return false;
  return document.querySelector('[role="dialog"]') !== null;
};

interface PendingWaiversBannerProps {
  /** Only members are asked; staff never see this. */
  enabled: boolean;
}

const PendingWaiversBanner: React.FC<PendingWaiversBannerProps> = ({ enabled }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [waivers, setWaivers] = useState<PendingWaiver[]>([]);
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  // Shown already for this sign-in (read once from storage, then kept in
  // memory so a blocked sessionStorage cannot make it reopen).
  const shownRef = useRef<boolean>(wasShownThisSignIn());
  const onPaymentPage = isPaymentPath(location.pathname);

  useEffect(() => {
    if (!enabled || shownRef.current) return;
    const token = localStorage.getItem("authToken");
    if (!token) return;
    let cancelled = false;
    getMyPendingWaivers(token)
      .then((list) => {
        if (!cancelled) setWaivers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Deliberately quiet: this is a reminder, and the request is already
        // reported by the axios interceptor. Showing nothing is the safe
        // failure — an error pop-up here could land on top of a pay screen.
        if (!cancelled) setWaivers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Open once: when there is something to sign, not on a payment page, and
  // not on top of another dialog. If either is in the way, it waits for the
  // next page instead (this effect re-runs when the path changes).
  useEffect(() => {
    if (!enabled || open || shownRef.current) return;
    if (waivers.length === 0 || onPaymentPage) return;
    if (anotherDialogIsOpen()) return;
    shownRef.current = true;
    markShown(); // BEFORE it is dismissed: a reload must not bring it back
    setOpen(true);
  }, [enabled, open, waivers.length, onPaymentPage, location.pathname]);

  // Never over a payment page, however the parent got there.
  useEffect(() => {
    if (open && onPaymentPage) setOpen(false);
  }, [open, onPaymentPage]);

  const handleLater = useCallback(() => {
    shownRef.current = true;
    markShown();
    setReviewing(false);
    setOpen(false);
  }, []);

  if (!enabled || !open || onPaymentPage || waivers.length === 0) return null;

  const handleSign = (waiverId: string) => async (answers: SignAnswers) => {
    const token = localStorage.getItem("authToken") || "";
    const shown = waivers.find((w) => w.waiver_id === waiverId);
    try {
      // The version this pop-up displayed goes with the signature: the server
      // refuses it if the club changed the wording in between.
      await signMyWaiver(token, waiverId, answers, shown?.version_id ?? null);
    } catch (err: any) {
      throw new Error(waiverErrorMessage(err, "We could not record your signature. Please try again."));
    }
    // The last one signed empties the list, which closes the pop-up.
    setWaivers((prev) => prev.filter((w) => w.waiver_id !== waiverId));
  };

  const count = waivers.length;
  const title = count === 1 ? "You have 1 form to sign" : `You have ${count} forms to sign`;

  return (
    <Modal isOpen onClose={handleLater} title={title} maxWidthClass="max-w-2xl">
      <div data-testid="pending-waivers-popup">
        <div className="flex items-start gap-3">
          <FileText className="w-6 h-6 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className={`font-medium ${getPrimaryText(isDarkMode)}`}>
              {waivers.map((w) => w.name).join(", ")}
            </p>
            <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
              It takes about a minute. You can also do this later; nothing else on your account is
              held up.
            </p>
          </div>
        </div>

        {reviewing && (
          <div className="mt-4 space-y-6 max-h-[60vh] overflow-y-auto pr-1">
            {waivers.map((waiver) => (
              <div key={waiver.waiver_id}>
                <h3 className={`text-lg font-bold mb-1 ${getPrimaryText(isDarkMode)}`}>{waiver.name}</h3>
                {waiver.reason === "out_of_date" && (
                  <p className={`text-sm mb-2 ${getSecondaryText(isDarkMode)}`}>
                    This form has changed since you last signed it.
                  </p>
                )}
                <WaiverSignForm waiver={waiver} onSign={handleSign(waiver.waiver_id)} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" size="md" onClick={handleLater}>
            Later
          </Button>
          {!reviewing && (
            <Button type="button" variant="primary" size="md" onClick={() => setReviewing(true)}>
              Review and sign
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PendingWaiversBanner;
